/**
 * Nome de unidade como o staff precisa ler: "Açougue e Avicola Frias · Loja 01".
 *
 * Unidade se chama "Loja 01", "Lab", "Matriz" — nome que se repete entre
 * clientes. Quem é de um cliente só vê as próprias e o nome basta; o
 * super_admin vê todas, e "Loja 01" sozinho não diz de quem é. Uma função só
 * para todos os seletores e colunas, senão cada tela resolve de um jeito.
 */

import { useCallback, useMemo } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useTenants } from "@/hooks/queries";

interface UnidadeComCliente {
  id: string;
  name: string;
  tenant_id: string;
}

export function useRotuloDeUnidade() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);
  const clientes = useMemo(
    () => new Map((tenants.data ?? []).map((t) => [t.id, t.name])),
    [tenants.data],
  );

  const rotulo = useCallback(
    (u: UnidadeComCliente): string => {
      const cliente = auth.isSuperAdmin ? clientes.get(u.tenant_id) : undefined;
      return cliente ? `${cliente} · ${u.name}` : u.name;
    },
    [auth.isSuperAdmin, clientes],
  );

  /** Opções de <Select>, ordenadas pelo rótulo — agrupa por cliente. */
  const opcoes = useCallback(
    (unidades: UnidadeComCliente[] | undefined) =>
      (unidades ?? [])
        .map((u) => ({ value: u.id, label: rotulo(u) }))
        .sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
    [rotulo],
  );

  return { rotulo, opcoes };
}
